import { JYOTISHA_MARGA_TAB_GROUPS } from './jyotishaMargaTabGroups';
import { JYOTISHA_TOPIC_SEARCH_INDEX } from './jyotishaTopicSearchIndex';
import { LAGNA_BASELINE_PAGES } from './jyotishaLagnaBaselinePages';
import { formatPhaladeepikaTopicLabel } from '../utils/phaladeepikaAccess';

const INDEX_BY_ID = Object.fromEntries(JYOTISHA_TOPIC_SEARCH_INDEX.map((entry) => [entry.id, entry]));

const MARGA_TAB_BY_ID = Object.fromEntries(
  JYOTISHA_MARGA_TAB_GROUPS.flatMap((group) =>
    group.tabs.map((tab) => [
      tab.id,
      {
        hi: tab.hi,
        en: tab.en,
        groupLabel: group.label,
        groupLabelEn: group.labelEn,
      },
    ])
  )
);

const LAGNA_BASELINE_BY_ID = Object.fromEntries(
  LAGNA_BASELINE_PAGES.map((page) => [
    page.topicId,
    {
      hi: page.lagnaHi,
      en: page.lagnaEn,
      groupLabel: 'चार्ट पठन',
      groupLabelEn: 'Reading Charts',
    },
  ])
);

/** English lesson title (matches search / topic cards). */
export function getJyotishaTopicLabel(topicId) {
  const raw = INDEX_BY_ID[topicId]?.tabLabel ?? MARGA_TAB_BY_ID[topicId]?.en ?? LAGNA_BASELINE_BY_ID[topicId]?.en ?? topicId;
  return formatPhaladeepikaTopicLabel(topicId, raw);
}

/** Syllabus topic group label for a lesson. */
export function getJyotishaTopicGroupLabel(topicId) {
  const fromIndex = INDEX_BY_ID[topicId]?.groupLabel;
  if (fromIndex) return fromIndex;
  const marga = MARGA_TAB_BY_ID[topicId];
  if (marga) return `${marga.groupLabel} (${marga.groupLabelEn})`;
  const lagna = LAGNA_BASELINE_BY_ID[topicId];
  if (lagna) return `${lagna.groupLabel} (${lagna.groupLabelEn})`;
  return '';
}

/**
 * @param {string} label
 * @returns {{ hi: string; en: string }}
 */
function splitBilingualGroupLabel(label) {
  const match = String(label).match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (match) {
    return { hi: match[1].trim(), en: match[2].trim() };
  }
  return { hi: label, en: label };
}

/** Hindi syllabus group (chapter) label. */
export function getJyotishaTopicGroupLabelHi(topicId) {
  const marga = MARGA_TAB_BY_ID[topicId];
  if (marga) return marga.groupLabel;
  const lagna = LAGNA_BASELINE_BY_ID[topicId];
  if (lagna) return lagna.groupLabel;
  const fromIndex = INDEX_BY_ID[topicId]?.groupLabel;
  if (fromIndex) return splitBilingualGroupLabel(fromIndex).hi;
  return '';
}

/** English syllabus group (chapter) label. */
export function getJyotishaTopicGroupLabelEn(topicId) {
  const marga = MARGA_TAB_BY_ID[topicId];
  if (marga) return marga.groupLabelEn;
  const lagna = LAGNA_BASELINE_BY_ID[topicId];
  if (lagna) return lagna.groupLabelEn;
  const fromIndex = INDEX_BY_ID[topicId]?.groupLabel;
  if (fromIndex) return splitBilingualGroupLabel(fromIndex).en;
  return '';
}

/** Toast title: `Topic - Lesson`. */
export function formatJyotishaTopicToastTitle(topicId, showEnglish = false) {
  const group = showEnglish ? getJyotishaTopicGroupLabelEn(topicId) : getJyotishaTopicGroupLabelHi(topicId);
  const lesson = showEnglish ? getJyotishaTopicLabel(topicId) : getJyotishaTopicLabelHi(topicId);
  if (group && lesson) return `${group} - ${lesson}`;
  return lesson || group || topicId;
}

/** Hindi lesson title when available. */
export function getJyotishaTopicLabelHi(topicId) {
  const raw = MARGA_TAB_BY_ID[topicId]?.hi ?? LAGNA_BASELINE_BY_ID[topicId]?.hi ?? INDEX_BY_ID[topicId]?.tabLabel ?? topicId;
  return formatPhaladeepikaTopicLabel(topicId, raw);
}

/** One-line chip: `Group · Lesson` using syllabus names. */
export function formatJyotishaTopicChip(topicId) {
  const group = getJyotishaTopicGroupLabel(topicId);
  const lesson = getJyotishaTopicLabel(topicId);
  return group ? `${group} · ${lesson}` : lesson;
}

/** Two-line chip label for inline related-topic buttons. */
export function formatJyotishaTopicLinkLines(topicId) {
  const hi = getJyotishaTopicLabelHi(topicId);
  const en = getJyotishaTopicLabel(topicId);
  const group = getJyotishaTopicGroupLabel(topicId);
  return {
    primary: hi !== en ? hi : en,
    secondary: hi !== en ? en : group || en,
    group,
  };
}
